import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { StoreData } from '../reducers/typings'
import { getLocations } from './api'
import { ILocation, LocationPageState } from './typings'
import { LocationFilterSet, defaultFilters, orderByOptions } from './filters'
import { normalizeFilters } from '@user/components/Filters'

const locationAdapter = createEntityAdapter<ILocation>()

export const initialState = locationAdapter.getInitialState<LocationPageState>({
  page: 1,
  perPage: 10,
  count: 0,
  loading: true,
  filterSet: defaultFilters,
})

const locations = createSlice({
  name: 'locations',
  initialState,
  reducers: {
    setLocationPage: (state, { payload: page }: PayloadAction<number>) => {
      state.page = page > 0 ? page : 1
    },
    setLocationFilters: (
      state,
      { payload }: PayloadAction<LocationFilterSet>,
    ) => {
      state.page = 1
      state.filterSet = normalizeFilters<ILocation, LocationFilterSet>(payload)
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getLocations.pending, (state) => {
      state.loading = true
    })
    builder.addCase(getLocations.rejected, (state) => {
      state.loading = false
      state.count = 0
      state.page = 1
      locationAdapter.removeAll(state)
    })
    builder.addCase(getLocations.fulfilled, (state, { payload }) => {
      const { '@odata.count': count, value } = payload
      state.loading = false
      state.count = count ?? 0
      locationAdapter.setAll(state, value)
    })
  },
})

export default locations.reducer

export const { setLocationPage, setLocationFilters } = locations.actions

const locationAdapterSelectors = locationAdapter.getSelectors<StoreData>(
  (state) => state.locations,
)

export const { selectAll: selectAllLocations } = locationAdapterSelectors

export const selectLocationPageState = (state: StoreData) => {
  const {
    locations: { page, perPage, count, loading },
  } = state
  return {
    page,
    pages: Math.ceil(count / perPage),
    count,
    loading,
  }
}

export const selectLocationFilters = (state: StoreData) => {
  const {
    locations: { filterSet },
  } = state
  return { filterSet, defaultFilters, orderByOptions }
}
